import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getProfile, type ProfileResponse } from "~/api/auth"
import { chooseFreeActiveDog } from "~/api/dogs"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { PawPrint } from "lucide-react"

export function ChooseFreeDogPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    getProfile()
      .then((data) => {
        if (!data.user.needs_free_dog_choice) {
          navigate("/dashboard", { replace: true })
          return
        }
        setProfile(data)
      })
      .catch(() => navigate("/login", { replace: true }))
  }, [navigate])

  async function handleConfirm() {
    if (!selectedId) return
    setBusy(true)
    setError(null)
    try {
      await chooseFreeActiveDog(selectedId)
      navigate("/dashboard", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save choice")
      setBusy(false)
    }
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-main)] p-4">
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </div>
    )
  }

  const dogs = profile.dogs ?? []

  return (
    <div className="min-h-screen bg-[var(--color-surface-main)] pb-24">
      <main className="mx-auto max-w-lg space-y-4 p-4 pt-8">
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Choose your Free active dog
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Free includes one active dog. Your other dogs and their memories stay safe and return if
          you reactivate Plus. Nothing will be deleted.
        </p>

        <ul className="space-y-2">
          {dogs.map((dog) => {
            const portraitSrc = heroPortraitUrl(dog.archetype_icon, dog.portrait)
            const selected = selectedId === dog.id
            return (
              <li key={dog.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(dog.id)}
                  aria-pressed={selected}
                  className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left ${
                    selected
                      ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)]/10"
                      : "border-[var(--color-divider)] bg-[var(--color-surface-card)]"
                  }`}
                >
                  {portraitSrc ? (
                    <img src={portraitSrc} alt="" className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--color-divider)]">
                      <PawPrint size={20} />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-[var(--color-text-primary)]">{dog.name}</p>
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      {dog.archetype_label}
                    </p>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={!selectedId || busy}
          onClick={() => void handleConfirm()}
          className="w-full rounded-lg bg-[var(--color-brand-primary)] px-4 py-3 font-medium text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Continue with this dog"}
        </button>

        <div className="flex justify-center gap-4 text-sm">
          <button type="button" onClick={() => navigate("/account")} className="underline">
            Account
          </button>
          <button type="button" onClick={() => navigate("/privacy")} className="underline">
            Privacy
          </button>
          <button type="button" onClick={() => navigate("/subscription")} className="underline">
            Subscription
          </button>
        </div>
      </main>
    </div>
  )
}
